import { Chess } from 'chess.js';
import type { Key } from 'chessground/types';
import type { EndgameDetail } from './api';

/**
 * State machine immutable du Endgame Drill (T2 MVP2.5).
 *
 * Identique en philosophie au `tacticsDrillReducer` (cf. JSDoc complet là-bas) :
 *  - reducer pur, Chess instance reconstruite localement à chaque action
 *  - state porte uniquement `fen` (string), pas d'instance partagée mutable
 *  - coup adversaire dispatché via `useEffect(awaiting_engine)` + RAF côté
 *    composant (jamais dans le reducer)
 *
 * Différences vs Tactics :
 *  - la séquence `key_moves` est plate (joueur+moteur alternent) — le cursor
 *    parcourt tous les demi-coups ; on s'arrête à la fin
 *  - chaque coup correct rapporte XP (cf. EndgameDrillPage `XP_PER_MOVE = 15`).
 *    Le composant lit `state.cursor` à la transition pour décider quand
 *    incrémenter le compteur affiché.
 */

export type EndgameDrillState =
  | { kind: 'idle' }
  | {
      kind: 'awaiting_player';
      detail: EndgameDetail;
      fen: string;
      cursor: number;
      lastMove: [Key, Key] | undefined;
    }
  | {
      kind: 'awaiting_engine';
      detail: EndgameDetail;
      fen: string;
      cursor: number;
      lastMove: [Key, Key];
      nextEngineMove: string;
    }
  | {
      kind: 'success';
      detail: EndgameDetail;
      fen: string;
      cursor: number;
      lastMove: [Key, Key] | undefined;
      playedMove: string;
      expectedMove: string;
    }
  | {
      kind: 'fail';
      detail: EndgameDetail;
      fen: string;
      cursor: number;
      lastMove: [Key, Key] | undefined;
      expectedMove: string;
      playedMove: string;
    };

export type EndgameDrillAction =
  | { type: 'START_DRILL'; detail: EndgameDetail }
  | {
      type: 'MOVE_PLAYER';
      from: Key;
      to: Key;
      /**
       * Pièce de promotion UCI ('q'/'r'/'b'/'n') si le coup atteint la 1ère
       * ou 8ème rangée avec un pion. Cf. F-4 P1 chessboards : sans ce
       * champ, chess.js v1 throw sur les finales avec promotion
       * (typique : R+P vs R, finales de promotion forcée) → coup ignoré.
       */
      promotion?: 'q' | 'r' | 'b' | 'n';
    }
  | { type: 'MOVE_ENGINE' };

export function endgameDrillReducer(
  state: EndgameDrillState,
  action: EndgameDrillAction,
): EndgameDrillState {
  switch (action.type) {
    case 'START_DRILL': {
      return {
        kind: 'awaiting_player',
        detail: action.detail,
        fen: action.detail.fen,
        cursor: 0,
        lastMove: undefined,
      };
    }

    case 'MOVE_PLAYER': {
      if (state.kind !== 'awaiting_player') return state;
      const { detail, fen, cursor } = state;
      const expectedSan = detail.key_moves[cursor];
      if (!expectedSan) return state;

      const chess = new Chess(fen);
      let playedSan: string | null = null;
      try {
        // F-4 : `promotion` injecté conditionnellement (exactOptionalPropertyTypes).
        const res = chess.move({
          from: action.from,
          to: action.to,
          ...(action.promotion ? { promotion: action.promotion } : {}),
        });
        playedSan = res?.san ?? null;
      } catch {
        playedSan = null;
      }
      if (playedSan === null) return state;

      if (playedSan !== expectedSan) {
        return {
          kind: 'fail',
          detail,
          // Garde la FEN d'AVANT la tentative ratée (cf. tactics reducer).
          fen,
          cursor,
          lastMove: undefined,
          expectedMove: expectedSan,
          playedMove: playedSan,
        };
      }

      const nextCursor = cursor + 1;
      const opponentReply = detail.key_moves[nextCursor];

      if (!opponentReply) {
        return {
          kind: 'success',
          detail,
          fen: chess.fen(),
          cursor: nextCursor,
          lastMove: [action.from, action.to],
          playedMove: playedSan,
          expectedMove: expectedSan,
        };
      }

      return {
        kind: 'awaiting_engine',
        detail,
        fen: chess.fen(),
        cursor: nextCursor,
        lastMove: [action.from, action.to],
        nextEngineMove: opponentReply,
      };
    }

    case 'MOVE_ENGINE': {
      if (state.kind !== 'awaiting_engine') return state;
      const { detail, fen, cursor, nextEngineMove } = state;

      const chess = new Chess(fen);
      try {
        const res = chess.move(nextEngineMove);
        if (!res) return state;
      } catch {
        return state;
      }

      return {
        kind: 'awaiting_player',
        detail,
        fen: chess.fen(),
        cursor: cursor + 1,
        lastMove: undefined,
      };
    }

    default:
      return state;
  }
}

export const initialEndgameDrillState: EndgameDrillState = { kind: 'idle' };
